import { useEffect, useState } from "react";
import { Archive, ArchiveRestore, MessageSquarePlus, Search, Send, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { DashboardShell } from "../components/DashboardShell";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BOXES = [["inbox", "Inbox"], ["sent", "Sent"], ["unread", "Unread"], ["archived", "Archived"]];

export default function MessagesPage() {
  const { user } = useAuth();
  const [box, setBox] = useState("inbox");
  const [q, setQ] = useState("");
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null); // {conversation, messages}
  const [draft, setDraft] = useState("");
  const [contacts, setContacts] = useState([]);
  const [composing, setComposing] = useState(false);
  const [newContact, setNewContact] = useState("");
  const [newSubject, setNewSubject] = useState("");
  const [newText, setNewText] = useState("");
  const [newInternal, setNewInternal] = useState(false);
  const [sending, setSending] = useState(false);

  const loadConvs = () => {
    api.get("/conversations", { params: { box, q } }).then((r) => setConvs(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(loadConvs, [box, q]);
  useEffect(() => {
    api.get("/messages/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, []);

  const openConv = async (id) => {
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setActive(data);
      await api.post(`/conversations/${id}/read`);
      loadConvs();
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => openConv(active.conversation.id), 15000);
    return () => clearInterval(t);
  }, [active?.conversation?.id]);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await api.post(`/conversations/${active.conversation.id}/messages`, { text: draft.trim() });
      setDraft("");
      openConv(active.conversation.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  const startConversation = async () => {
    const contact = contacts.find((c) => `${c.user_id}|${c.case_id || ""}` === newContact);
    if (!contact || !newText.trim()) { toast.error("Choose a recipient and write a message."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/conversations", {
        recipient_id: contact.user_id, case_id: contact.case_id || null,
        internal: contact.internal_allowed ? newInternal : false,
        subject: newSubject.trim(), text: newText.trim(),
      });
      toast.success("Message sent.");
      setComposing(false); setNewText(""); setNewSubject(""); setNewInternal(false); setNewContact("");
      setBox("inbox");
      loadConvs();
      openConv(data.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  const toggleArchive = async (id) => {
    try {
      await api.post(`/conversations/${id}/archive`);
      setActive(null);
      loadConvs();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <DashboardShell title="Messages" subtitle="Secure communication tied to your evaluations. Internal staff messages are never visible to clients."
      actions={<Button data-testid="compose-btn" onClick={() => setComposing(!composing)}
        className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
        <MessageSquarePlus className="h-4 w-4 mr-2" /> New message
      </Button>}>
      {composing && (
        <div className="card-warm p-6 mb-6 max-w-3xl space-y-4" data-testid="compose-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Recipient</p>
              <Select value={newContact} onValueChange={setNewContact}>
                <SelectTrigger data-testid="compose-recipient" className="border-[#E5E5DF]"><SelectValue placeholder="Choose recipient…" /></SelectTrigger>
                <SelectContent>
                  {contacts.map((c) => (
                    <SelectItem key={`${c.user_id}|${c.case_id || ""}`} value={`${c.user_id}|${c.case_id || ""}`}>
                      {c.name} ({c.role}{c.case_label ? ` · ${c.case_label}` : ""})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Subject (optional)</p>
              <Input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} data-testid="compose-subject" className="input-warm border-[#E5E5DF]" />
            </div>
          </div>
          <Textarea rows={3} value={newText} onChange={(e) => setNewText(e.target.value)} data-testid="compose-text"
            placeholder="Type your message…" className="input-warm border-[#E5E5DF] text-sm" />
          {user?.role !== "client" && newContact && contacts.find((c) => `${c.user_id}|${c.case_id || ""}` === newContact)?.internal_allowed && (
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <Checkbox checked={newInternal} onCheckedChange={(v) => setNewInternal(!!v)} data-testid="compose-internal" />
              <span className="flex items-center gap-1.5"><ShieldAlert className="h-4 w-4 text-[#8C2A2A]" /> Internal staff message — never visible to clients</span>
            </label>
          )}
          <div className="flex gap-2">
            <Button onClick={startConversation} disabled={sending} data-testid="compose-send-btn"
              className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white">{sending ? "Sending…" : "Send"}</Button>
            <Button variant="ghost" onClick={() => setComposing(false)} className="rounded-full">Cancel</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-warm overflow-hidden h-fit" data-testid="conversation-list">
          <div className="p-4 border-b border-[#E5E5DF] space-y-3">
            <div className="flex gap-1 flex-wrap">
              {BOXES.map(([v, l]) => (
                <button key={v} onClick={() => { setBox(v); setActive(null); }} data-testid={`box-${v}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${box === v ? "bg-[#1E3F20] text-white" : "bg-[#E3DAC9]/50 text-[#5C5C57] hover:bg-[#E3DAC9]"}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-[#5C5C57]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="message-search"
                className="pl-9 input-warm border-[#E5E5DF] h-9 text-sm" />
            </div>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {convs === null ? (
              <div className="p-6"><div className="h-6 w-6 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" /></div>
            ) : convs.length === 0 ? (
              <p className="p-6 text-sm text-[#5C5C57]" data-testid="no-conversations">No conversations here.</p>
            ) : convs.map((c) => (
              <button key={c.id} onClick={() => openConv(c.id)} data-testid={`conv-${c.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#E5E5DF] last:border-0 hover:bg-[#FAFAF8] transition-colors ${active?.conversation?.id === c.id ? "bg-[#1E3F20]/5" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-sm truncate">{c.other?.name}</p>
                  {c.unread_count > 0 && (
                    <span className="shrink-0 h-5 min-w-5 px-1.5 rounded-full bg-[#1E3F20] text-white text-[11px] font-bold flex items-center justify-center" data-testid={`unread-${c.id}`}>
                      {c.unread_count}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {c.internal ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#8C2A2A]/10 text-[#8C2A2A]">INTERNAL</span>
                  ) : (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#7E9B82]/15 text-[#1E3F20]">CLIENT</span>
                  )}
                  <p className="text-xs text-[#5C5C57] truncate">{c.case_label || c.subject || "General"}</p>
                </div>
                <p className="text-[11px] text-[#5C5C57] mt-1">{new Date(c.last_message_at).toLocaleString()}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 card-warm flex flex-col min-h-[480px]" data-testid="thread-panel">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#5C5C57] p-10">Select a conversation, or start a new one.</div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-[#E5E5DF] flex items-center justify-between">
                <div>
                  <p className="font-heading text-xl font-semibold" data-testid="thread-title">{active.conversation.other?.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {active.conversation.internal ? (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#8C2A2A]/10 text-[#8C2A2A]" data-testid="thread-internal-badge">INTERNAL MESSAGE</span>
                    ) : (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#7E9B82]/15 text-[#1E3F20]" data-testid="thread-client-badge">CLIENT MESSAGE</span>
                    )}
                    {active.conversation.case_label && <span className="text-xs text-[#5C5C57]">{active.conversation.case_label}</span>}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => toggleArchive(active.conversation.id)} data-testid="archive-btn"
                  className="rounded-full text-[#5C5C57]">
                  {active.conversation.archived ? <><ArchiveRestore className="h-4 w-4 mr-1.5" /> Unarchive</> : <><Archive className="h-4 w-4 mr-1.5" /> Archive</>}
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4 max-h-[420px]">
                {active.messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${mine ? "bg-[#1E3F20] text-white" : "bg-[#E3DAC9]/60 text-[#1C1C19]"}`}>
                        <p className="text-sm leading-6 whitespace-pre-wrap">{m.text}</p>
                        <p className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-[#5C5C57]"}`}>
                          {m.sender_name} · {new Date(m.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-4 border-t border-[#E5E5DF] flex gap-2">
                <Textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  data-testid="reply-input" placeholder="Type message here…" className="input-warm border-[#E5E5DF] text-sm" />
                <Button onClick={send} disabled={sending || !draft.trim()} data-testid="reply-send-btn"
                  className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white self-end">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
